import {
  COLORING_GRID_PRICE_LIST,
  COLORING_GRID_STATUSES,
  ColoringGridStatusConstants,
} from 'app-constants';
import { doc, getDoc } from 'firebase/firestore';
import { useFirstRender } from 'hooks';
import { DashboardRoutePath } from 'modules/dashboard/navigation';
import React, { useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Splash } from 'shared/Components';
import styled from 'styled-components';

import { firestore } from '../../../../services';
import ColoringGridImage from '../../../../shared/Components/ColoringGridImage/ColoringGridImage';
import {
  InformationModal,
  InformationModalTypes,
} from '../../../../shared/Components/InformationModal';
import { Box, FlexContainer, Image, Text } from '../../../../shared/Typhography';

interface ParamTypes {
  id: string;
}

const LabelWrapper = styled.div`
  margin: 5px;
`;

const Badge = styled.div<{ background: string }>`
  padding: 5px;
  background: ${({ background }) => background};
  border-radius: 6px;
  color: white;
  width: max-content;
  margin: 0 4px 0 0;
`;

export default function ViewColoringGridScreen() {
  const { id } = useParams<ParamTypes>();

  const isFirstRender = useFirstRender();
  const navigate = useHistory();

  const [collections, setCollections] = React.useState<any>(null);
  const [artist, setArtist] = React.useState<any>(null);
  const [coloringGrid, setColoringGrid] = React.useState<any>(null);

  const haveCollections = !!coloringGrid?.collections?.length;

  const fetchColoringGrid = async () => {
    const docRef = doc(firestore, `coloringGrids/${id}`);
    const docData = await getDoc(docRef);
    setColoringGrid({
      uuid: docData.id,
      ...docData.data(),
    });
  };

  const fetchCollections = async (ids: string[]) => {
    const tmpCollections = [];
    for (let i = 0; i < ids.length; i++) {
      const docRef = doc(firestore, `coloringGridCollections/${ids[i]}`);
      const docData = await getDoc(docRef);
      tmpCollections.push({
        uuid: docData.id,
        ...docData.data(),
      });
    }

    setCollections(tmpCollections);
  };

  const fetchArtist = async (artistId: string) => {
    const docRef = doc(firestore, `usersWeb/${artistId}`);
    const docData = await getDoc(docRef);
    setArtist({
      uuid: docData.id,
      ...docData.data(),
    });
  };

  React.useEffect(() => {
    if (!isFirstRender) return;

    fetchColoringGrid();
  }, [isFirstRender]);

  React.useEffect(() => {
    if (!coloringGrid?.uuid) return;

    fetchArtist(coloringGrid?.publisher);
    if (haveCollections) fetchCollections(coloringGrid.collections);
  }, [coloringGrid]);

  const previewImageUrl = useMemo(() => coloringGrid?.iconUrl, [coloringGrid]);

  const previewImageUrlLink = useMemo(() => {
    return `${window.location.origin}/${
      DashboardRoutePath.CONTENT_PREVIEW_SCREEN
    }?type=coloringGrid&url=${encodeURI(coloringGrid?.url)}`;
  }, [coloringGrid]);

  const tierLabel = useMemo((): string => {
    return (
      COLORING_GRID_PRICE_LIST.find((price) => price.value === coloringGrid?.premiumTier)?.label ||
      'Free'
    );
  }, [coloringGrid]);

  return (
    <>
      {coloringGrid && (
        <FlexContainer
          flexDirection="row"
          margin="150px auto 50px"
          justifyContent="center"
          gap="20px"
          padding="32px"
          flexWrap="wrap"
        >
          <FlexContainer flexDirection="column" alignItems="center" gap="20px" flexWrap="wrap">
            {coloringGrid.status === COLORING_GRID_STATUSES.deleted ? (
              <ColoringGridImage
                src={previewImageUrl}
                alt="Picture"
                width="256px"
                maxWidth="256px"
                mobWidth="256px"
              />
            ) : (
              <a href={previewImageUrlLink} target="_blank" rel="noreferrer">
                <ColoringGridImage
                  cursor="pointer"
                  src={previewImageUrl}
                  alt="Picture"
                  width="256px"
                  maxWidth="256px"
                  mobWidth="256px"
                />
              </a>
            )}

            {coloringGrid?.status !== COLORING_GRID_STATUSES.deleted && (
              <>
                <Button
                  onClick={() => navigate.push(`/manage-coloring-grids/${coloringGrid?.uuid}`)}
                  style={{ width: '100%', height: '32px' }}
                >
                  Edit Coloring Grid
                </Button>
              </>
            )}
          </FlexContainer>

          <FlexContainer flexDirection="column" flex="1" gap="20px">
            <Box margin="0" width="100%">
              <Text weight="bold" size={1.5}>
                Coloring Grid Info
              </Text>
              <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Name
                  </Text>
                  <Text size={1.5}>{coloringGrid?.name}</Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Status
                  </Text>

                  {/* TODO Add more status colors when self service is implemented */}
                  <div style={{ display: 'flex' }}>
                    <Badge
                      background={
                        (ColoringGridStatusConstants as any)?.[
                          coloringGrid?.status || COLORING_GRID_STATUSES.approved
                        ]?.color
                      }
                    >
                      {coloringGrid?.status}
                    </Badge>
                    {(coloringGrid?.status === COLORING_GRID_STATUSES.rejected ||
                      coloringGrid?.status === COLORING_GRID_STATUSES.edited) && (
                      <InformationModal
                        type={InformationModalTypes.Error}
                        title="Rejection reason"
                        content={coloringGrid.rejectMessage}
                      />
                    )}
                  </div>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Plays
                  </Text>
                  <Text size={1.5}>0</Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Likes
                  </Text>
                  <Text size={1.5}>0</Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Keywords
                  </Text>
                  <FlexContainer>
                    {coloringGrid?.keywords?.map((keyword: string) => (
                      <Badge background="#585CEA" key={keyword}>
                        {keyword}
                      </Badge>
                    ))}
                  </FlexContainer>
                </LabelWrapper>
              </FlexContainer>
            </Box>
            <Box margin="0" width="100%">
              <Text weight="bold" size={1.5}>
                Coloring Grid Categories
              </Text>
              <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                {collections && (
                  <>
                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        Categories
                      </Text>
                      <FlexContainer>
                        {collections?.map((collection: any) => (
                          <Badge background="#585CEA" key={collection.uuid}>
                            {collection.name}
                          </Badge>
                        ))}
                      </FlexContainer>
                    </LabelWrapper>

                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        AI Generated
                      </Text>
                      <Text size={1.5}>{coloringGrid?.aiGenerated ? 'True' : 'False'}</Text>
                    </LabelWrapper>
                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        Price
                      </Text>
                      <Text size={1.5}>{tierLabel}</Text>
                    </LabelWrapper>
                  </>
                )}
                {haveCollections && !collections && <Splash width={64} />}
              </FlexContainer>
            </Box>
            <Box margin="0" width="100%">
              <Text weight="bold" size={1.5}>
                Coloring Grid Metadata
              </Text>
              <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Resolution
                  </Text>
                  <Text size={1.5}>{coloringGrid?._meta?.originalResolution}</Text>
                </LabelWrapper>
                <LabelWrapper />
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Created At
                  </Text>
                  <Text size={1.5}>
                    {coloringGrid?.createdAt.toDate().toGMTString().replace('GMT', '')}
                  </Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Published At
                  </Text>
                  <Text size={1.5}>
                    {coloringGrid?.publishedAt
                      ? coloringGrid?.publishedAt.toDate().toGMTString().replace('GMT', '')
                      : 'Not published'}
                  </Text>
                </LabelWrapper>
              </FlexContainer>
            </Box>
            <Box margin="0" width="100%">
              <Text weight="bold" size={1.5}>
                Artist Info
              </Text>
              <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                {artist && (
                  <>
                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        Profile Image
                      </Text>
                      <Image
                        src={artist?.profileImageUrl}
                        borderRadiusType="circle"
                        alt="Picture"
                        maxWidth="128px"
                        maxHeight="128px"
                        mobWidth="92px"
                        mobHeight="92px"
                      />
                    </LabelWrapper>
                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        Name
                      </Text>
                      <Text size={1.5}>{artist?.Name ?? artist?.name}</Text>
                    </LabelWrapper>
                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        Revenue Share
                      </Text>
                      <Text size={1.5}>{artist?.revenueShare * 100}%</Text>
                    </LabelWrapper>
                  </>
                )}
                {!artist && <Splash width={64} />}
              </FlexContainer>
            </Box>
            <Box margin="0" width="100%">
              <Text weight="bold" size={1.5}>
                Insights
              </Text>
              <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Revenue
                  </Text>
                  <Text size={1.5}>$ 0</Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Artist Royalties
                  </Text>
                  <Text size={1.5}>$ 0</Text>
                </LabelWrapper>
              </FlexContainer>
            </Box>
          </FlexContainer>
        </FlexContainer>
      )}

      {!coloringGrid && <Splash />}
    </>
  );
}
